import apiClient from './api';
import * as XLSX from 'xlsx';
import { API_ENDPOINTS } from '@/config/api.config';
import type {
  APIResponse,
  CertificationPerformanceMetricsResponse,
  CertificationPerformanceListResponse,
  CertificationPerformanceResponse,
  CertificationPerformanceListParams,
  CertificationPerformanceMetricsDto,
  CertificationPerformanceListResponseDto,
  CertificationPerformanceItemDto,
  CertificationUsersListParams,
  CertificationDataResponse,
  CertificationDataResponses,
  CertificationUserData,
  UserCertificationProgressResponse,
  UserCertificationProgressDto,
  UserCertificationDetailsResponse,
  UserCertificationDetailsDto,
  UserCertificationAssignment,
  ExamAttemptDetailed,
  ExamAttemptsListResponse,
  ExamAttemptApiResponse,
  QuestionAttempt,
  CertificationUserAnalytics,
  CertificationUserAnalyticsResponse
} from '@/types/certification-performance.types';

/**
 * Service for managing certification performance analytics
 * Provides metrics, list views, and detailed certification performance data
 */
class CertificationPerformanceService {
  /**
   * Get high-level certification performance metrics
   * Returns total active certifications, enrollments, and average pass rate
   * @returns Promise with certification performance metrics
   */
  async getMetrics(params?: import('@/types/certification-performance.types').CertificationPerformanceMetricsParams): Promise<CertificationPerformanceMetricsDto> {
    try {
      const body: any = {};

      if (params?.granularity) {
        body.granularity = params.granularity;
      }
      if (params?.start_date) {
        body.start_date = params.start_date;
      }
      if (params?.end_date) {
        body.end_date = params.end_date;
      }

      const response = await apiClient.post<CertificationPerformanceMetricsResponse>(
        API_ENDPOINTS.CERTIFICATION_PERFORMANCE.METRICS,
        body
      );

      if (!response.data.success) {
        throw new Error(response.data.description || 'Failed to fetch certification performance metrics');
      }

      return response.data.data;
    } catch (error) {
      console.error('Error fetching certification performance metrics:', error);
      throw error;
    }
  }

  /**
   * Get paginated list of certification performance data with filtering and sorting
   * @param params - Query parameters for filtering, sorting, and pagination
   * @returns Promise with paginated certification performance list
   */
  async getCertificationPerformanceList(params?: CertificationPerformanceListParams): Promise<CertificationPerformanceListResponseDto> {
    try {
      const body: any = {};

      // Add parameters to body if provided
      if (params?.page !== undefined) {
        body.page = params.page;
      }
      if (params?.limit !== undefined) {
        body.limit = params.limit;
      }
      if (params?.status) {
        body.status = params.status;
      }
      if (params?.search) {
        body.search = params.search;
      }
      if (params?.sortBy) {
        body.sortBy = params.sortBy;
      }
      if (params?.sortOrder) {
        body.sortOrder = params.sortOrder;
      }
      if (params?.granularity) {
        body.granularity = params.granularity;
      }
      if (params?.start_date) {
        body.start_date = params.start_date;
      }
      if (params?.end_date) {
        body.end_date = params.end_date;
      }
      if (params?.categoryId) {
        body.categoryId = params.categoryId;
      }
      if (params?.groupKey) {
        body.groupKey = params.groupKey;
      }
      if (params?.roleKey) {
        body.roleKey = params.roleKey;
      }

      const response = await apiClient.post<CertificationPerformanceListResponse>(
        API_ENDPOINTS.CERTIFICATION_PERFORMANCE.LIST,
        body
      );

      if (!response.data.success) {
        throw new Error(response.data.description || 'Failed to fetch certification performance list');
      }

      return response.data.data;
    } catch (error) {
      console.error('Error fetching certification performance list:', error);
      throw error;
    }
  }

  /**
   * Get users enrolled in a specific certification with their performance data
   * @param certificationId - The unique identifier of the certification
   * @param params - Query parameters for pagination and search
   * @returns Promise with certification user data
   */
  async getCertificationUsers(certificationId: string, params: CertificationUsersListParams): Promise<CertificationDataResponses> {
    try {
      const body: any = {};
      if (params.page) body.page = params.page;
      if (params.limit) body.limit = params.limit;
      if (params.search) body.search = params.search;
      if (params.status) body.status_filter = params.status;

      const url = API_ENDPOINTS.CERTIFICATION_PERFORMANCE.GET_USER_BASED_ON_CERTIFICATION(certificationId);
      const response = await apiClient.post<CertificationDataResponses>(url, body);

      if (!response.data.success) {
        throw new Error(response.data.description || 'Failed to fetch certification users');
      }

      return response.data;
    } catch (error) {
      console.error(`Error fetching users for certification ID ${certificationId}:`, error);
      throw error;
    }
  }

  /**
   * Get certification user analytics including total users, pass count, pass rate, and average score
   * @param certificationId - The unique identifier of the certification
   * @returns Promise with certification user analytics
   */
  async getCertificationUserAnalytics(certificationId: string): Promise<CertificationUserAnalytics> {
    try {
      const url = API_ENDPOINTS.CERTIFICATION_PERFORMANCE.GET_ANALYTICS(certificationId);
      const response = await apiClient.get<CertificationUserAnalyticsResponse>(url);

      if (!response.data.success) {
        throw new Error(response.data.description || 'Failed to fetch certification user analytics');
      }

      return response.data.data;
    } catch (error) {
      console.error(`Error fetching analytics for certification ID ${certificationId}:`, error);
      throw error;
    }
  }

  /**
   * Get performance data for a specific certification
   * @param certificationId - The unique identifier of the certification
   * @returns Promise with certification performance data or null if not found
   */
  async getCertificationPerformanceById(certificationId: string): Promise<CertificationPerformanceItemDto | null> {
    try {
      const response = await apiClient.get<CertificationPerformanceResponse>(
        API_ENDPOINTS.CERTIFICATION_PERFORMANCE.GET_BY_ID(certificationId)
      );

      if (!response.data.success) {
        // Check if it's a 404 (certification not found)
        if (response.data.code === 404) {
          return null;
        }
        throw new Error(response.data.description || 'Failed to fetch certification performance');
      }

      return response.data.data;
    } catch (error) {
      console.error(`Error fetching certification performance for ID ${certificationId}:`, error);
      throw error;
    }
  }

  /**
   * Search certifications by name
   * @param searchTerm - The search term to filter certifications by name
   * @param limit - Maximum number of results to return
   * @returns Promise with filtered certification list
   */
  async searchCertifications(searchTerm: string, limit: number = 20): Promise<CertificationPerformanceListResponseDto> {
    return this.getCertificationPerformanceList({
      search: searchTerm,
      limit,
      page: 1,
      sortBy: 'name',
      sortOrder: 'asc'
    });
  }

  /**
   * Get top performing certifications by pass rate
   * @param limit - Number of top certifications to return
   * @returns Promise with top performing certifications
   */
  async getTopPerformingCertifications(limit: number = 10): Promise<CertificationPerformanceItemDto[]> {
    const result = await this.getCertificationPerformanceList({
      status: 'active',
      sortBy: 'pass_rate',
      sortOrder: 'desc',
      limit,
      page: 1
    });

    return result.certifications;
  }

  /**
   * Get certifications with low pass rates that may need attention
   * @param threshold - Pass rate percentage threshold (default 50%)
   * @param limit - Maximum number of certifications to return
   * @returns Promise with certifications needing attention
   */
  async getCertificationsNeedingAttention(threshold: number = 50, limit: number = 20): Promise<CertificationPerformanceItemDto[]> {
    const result = await this.getCertificationPerformanceList({
      status: 'active',
      sortBy: 'pass_rate',
      sortOrder: 'asc',
      limit,
      page: 1
    });

    // Filter certifications below the threshold
    return result.certifications.filter(cert => cert.pass_percentage < threshold);
  }

  /**
   * Get most popular certifications by enrollment
   * @param limit - Number of certifications to return
   * @returns Promise with most enrolled certifications
   */
  async getMostEnrolledCertifications(limit: number = 10): Promise<CertificationPerformanceItemDto[]> {
    const result = await this.getCertificationPerformanceList({
      status: 'active',
      sortBy: 'enrollments',
      sortOrder: 'desc',
      limit,
      page: 1
    });

    return result.certifications;
  }

  /**
   * Get certifications by status
   * @param status - Certification status to filter by
   * @param page - Page number for pagination
   * @param limit - Items per page
   * @returns Promise with filtered certification list
   */
  async getCertificationsByStatus(
    status: 'active' | 'inactive' | 'completed' | 'draft',
    page: number = 1,
    limit: number = 20
  ): Promise<CertificationPerformanceListResponseDto> {
    return this.getCertificationPerformanceList({
      status,
      page,
      limit,
      sortBy: 'name',
      sortOrder: 'asc'
    });
  }

  /**
   * Calculate overall learning effectiveness score for certifications
   * Based on pass rates, scores, and satisfaction
   * @returns Promise with effectiveness metrics
   */
  async calculateLearningEffectiveness(): Promise<{
    effectiveness_score: number;
    metrics: CertificationPerformanceMetricsDto;
    insights: string[];
  }> {
    try {
      const metrics = await this.getMetrics();
      const activeCertifications = await this.getCertificationsByStatus('active', 1, 100);

      // Calculate effectiveness score (0-100)
      let effectivenessScore = 0;
      let insights: string[] = [];

      // Factor 1: Pass rate (50% weight) - more important for certifications
      const passRateScore = metrics.avg_pass_rate * 0.5;
      effectivenessScore += passRateScore;

      if (metrics.avg_pass_rate > 80) {
        insights.push('Excellent pass rates across certifications');
      } else if (metrics.avg_pass_rate < 50) {
        insights.push('Low pass rates indicate potential difficulty or preparation issues');
      }

      // Factor 2: Average satisfaction (30% weight)
      if (activeCertifications.certifications.length > 0) {
        const avgSatisfaction = activeCertifications.certifications.reduce(
          (sum, cert) => sum + cert.satisfaction, 0
        ) / activeCertifications.certifications.length;
        const satisfactionScore = (avgSatisfaction / 5) * 100 * 0.3;
        effectivenessScore += satisfactionScore;

        if (avgSatisfaction >= 4) {
          insights.push('High learner satisfaction indicates quality certification exams');
        } else if (avgSatisfaction < 3) {
          insights.push('Low satisfaction scores suggest certification improvements needed');
        }
      }

      // Factor 3: Engagement (20% weight)
      if (metrics.total_active_certifications > 0 && metrics.total_enrollments > 0) {
        const engagementRatio = Math.min(
          metrics.total_enrollments / (metrics.total_active_certifications * 10),
          1
        );
        const engagementScore = engagementRatio * 100 * 0.2;
        effectivenessScore += engagementScore;

        if (metrics.total_enrollments / metrics.total_active_certifications > 20) {
          insights.push('Strong enrollment numbers show high certification demand');
        }
      }

      return {
        effectiveness_score: Math.round(effectivenessScore),
        metrics,
        insights
      };
    } catch (error) {
      console.error('Error calculating learning effectiveness:', error);
      throw error;
    }
  }

  /**
   * Export certification performance data to CSV format
   * @param filters - Optional filters to apply
   * @returns Promise with export data as Blob
   */
  async exportCertificationPerformance(
    filters?: CertificationPerformanceListParams
  ): Promise<Blob> {
    try {
      // Get all certifications with applied filters
      const data = await this.getCertificationPerformanceList({
        ...filters,
        limit: 1000 // Get maximum records for export
      });

      // Convert to CSV format
      const csvContent = this.convertToCSV(data.certifications);
      return new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    } catch (error) {
      console.error('Error exporting certification performance:', error);
      throw error;
    }
  }

  /**
   * Export certification user data using dedicated export API (no pagination)
   */
  async exportCertificationUserData(
    certificationId: string,
    format: 'csv' | 'excel',
    filters?: { search?: string; status?: string; certificationName?: string }
  ): Promise<{ success: boolean; filename: string; count: number; message?: string }> {
    const body: any = {};
    if (filters?.search) body.search = filters.search;
    if (filters?.status) body.status_filter = filters.status;

    const url = API_ENDPOINTS.EXPORT.CERTIFICATION_USER_DATA(certificationId);
    const response = await apiClient.post(url, body);

    const data = response.data?.data;
    const users: CertificationUserData[] = data?.users || [];

    if (users.length === 0) {
      return {
        success: false,
        filename: '',
        count: 0,
        message: 'No user data available to export with the current filters'
      };
    }

    const filename = this.generateCertificationUserDataFilename(format, filters);

    if (format === 'excel') {
      const blob = this.generateCertificationUserDataExcel(users);
      this.downloadBlob(blob, filename);
    } else {
      const csv = this.generateCertificationUserDataCSV(users);
      this.downloadCSVContent(csv, filename);
    }

    return { success: true, filename, count: users.length };
  }

  private formatSecondsToHHMMSS(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }

  private generateCertificationUserDataFilename(
    format: 'csv' | 'excel',
    filters?: { search?: string; status?: string; certificationName?: string }
  ): string {
    const timestamp = new Date().toISOString().split('T')[0];
    const parts = ['certification-user-data'];

    if (filters?.certificationName) {
      const sanitized = filters.certificationName.replace(/[^a-z0-9]/gi, '-').toLowerCase();
      parts.push(sanitized);
    }
    if (filters?.status && filters.status !== 'all') {
      parts.push(`status-${filters.status}`);
    }
    if (filters?.search) {
      const sanitized = filters.search.replace(/[^a-z0-9]/gi, '-').toLowerCase();
      parts.push(`search-${sanitized}`);
    }
    parts.push(timestamp);

    const ext = format === 'excel' ? 'xlsx' : 'csv';
    return parts.join('_') + `.${ext}`;
  }

  private generateCertificationUserDataCSV(users: CertificationUserData[]): string {
    const headers = ['First Name', 'Last Name', 'Email', 'Status', 'Attempts', 'High Score (%)', 'Passed', 'Time Spent (HH:MM:SS)'];
    const escape = (val: string | number) => {
      const s = String(val);
      return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = users.map(u => [
      escape(u.given_name),
      escape(u.family_name),
      escape(u.email),
      escape(u.status.replace(/_/g, ' ')),
      escape(u.exam_attempts),
      escape(u.latest_exam_percentage.toFixed(1)),
      escape(u.passed_exam ? 'Yes' : 'No'),
      escape(this.formatSecondsToHHMMSS(u.total_time_spent_seconds)),
    ].join(','));
    return [headers.join(','), ...rows].join('\n');
  }

  private generateCertificationUserDataExcel(users: CertificationUserData[]): Blob {
    const data = users.map(u => ({
      'First Name': u.given_name,
      'Last Name': u.family_name,
      'Email': u.email,
      'Status': u.status.replace(/_/g, ' '),
      'Attempts': u.exam_attempts,
      'High Score (%)': u.latest_exam_percentage.toFixed(1),
      'Passed': u.passed_exam ? 'Yes' : 'No',
      'Time Spent (HH:MM:SS)': this.formatSecondsToHHMMSS(u.total_time_spent_seconds),
    }));
    const worksheet = XLSX.utils.json_to_sheet(data);
    worksheet['!cols'] = [
      { wch: 15 }, { wch: 15 }, { wch: 30 }, { wch: 15 }, { wch: 10 }, { wch: 10 }, { wch: 8 }, { wch: 20 },
    ];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Certification Users');
    const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    return new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  }

  private downloadCSVContent(content: string, filename: string) {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  private downloadBlob(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  /**
   * Convert certification data to CSV format
   * @param certifications - Array of certification performance data
   * @returns CSV string
   */
  private convertToCSV(certifications: CertificationPerformanceItemDto[]): string {
    const headers = [
      'Certification ID',
      'Certification Name',
      'Status',
      'Total Enrollments',
      'Active Enrollments',
      'Pass Rate (%)',
      'Average Score',
      'Passed Users'
    ];

    const rows = certifications.map(cert => [
      cert.certification_id,
      `"${cert.certification_name}"`, // Quote to handle commas in names
      cert.status,
      cert.total_user_enrollments,
      cert.total_user_active_enrollments,
      cert.pass_percentage.toFixed(2),
      cert.avg_score.toFixed(2),
      cert.passed_user_count
    ]);

    return [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
  }

  /**
   * Get user certification progress for a specific certification
   * Calls backend method: getDetailedCertificationWithProgress
   * @param userId - The unique identifier of the user
   * @param certificationId - The unique identifier of the certification
   * @returns Promise with user certification progress data
   */
  async getUserCertificationProgress(
    userId: string,
    certificationId: string
  ): Promise<UserCertificationProgressDto> {
    try {
      // POST call with certification_id in body
      const url = API_ENDPOINTS.CERTIFICATION_TRACKING.USER_PROGRESS(userId);
      console.log('Fetching certification progress from:', url);

      const response = await apiClient.post<UserCertificationProgressResponse>(url, {
        certification_id: certificationId
      });
      console.log('Certification progress response:', response.data);

      if (!response.data.success) {
        throw new Error(response.data.description || 'Failed to fetch user certification progress');
      }

      const progressData = response.data.data;

      // Ensure attempts array exists
      if (!progressData.attempts) {
        progressData.attempts = [];
      }

      // Log the data for debugging
      console.log('Certification progress data:', {
        title: progressData.title,
        status: progressData.status,
        exam_attempts: progressData.exam_attempts,
        latest_percentage: progressData.latest_exam_percentage,
        attempts_count: progressData.attempts.length
      });

      return progressData;
    } catch (error) {
      console.error(`Error fetching certification progress for user ${userId}, certification ${certificationId}:`, error);
      throw error;
    }
  }

  /**
   * Get all certification details for a specific user (summary)
   * Includes all assigned certifications and their progress
   * Uses the same endpoint as getUserCertificationProgress but WITHOUT certificationId query param
   * @param userId - The unique identifier of the user
   * @returns Promise with user certification details
   */
  async getUserCertificationDetails(userId: string): Promise<UserCertificationDetailsDto> {
    try {
      // Call the same endpoint without certificationId to get summary of all certifications
      const url = API_ENDPOINTS.CERTIFICATION_TRACKING.USER_PROGRESS(userId);

      // API actually returns an array of UserCertificationAssignment directly in data
      const response = await apiClient.get<APIResponse<UserCertificationAssignment[]>>(url);

      if (!response.data.success) {
        throw new Error(response.data.description || 'Failed to fetch user certification details');
      }

      const certifications = response.data.data || [];

      // Transform each certification to add computed fields
      const transformedCertifications = certifications.map(cert => {
        // Calculate latest_exam_percentage if not provided
        let percentage = cert.latest_exam_percentage || 0;

        // If completed and passed but no percentage, use passing score or 100%
        if (cert.status === 'COMPLETED' && cert.passed_exam && percentage === 0) {
          percentage = 100; // Assume 100% for completed certifications
        } else if (cert.status === 'IN_PROGRESS' && percentage === 0 && cert.exam_attempts > 0) {
          percentage = cert.passing_score || 0; // Use passing score as estimate
        }

        return {
          ...cert,
          certification_name: cert.title,  // Map title to certification_name
          latest_exam_percentage: percentage,
          last_attempt_at: cert.last_accessed_at || cert.last_attempt_at,  // Use last_accessed_at if available
        };
      });

      // Calculate summary statistics
      const summary = {
        total_assigned: transformedCertifications.length,
        completed: transformedCertifications.filter(c => c.status === 'COMPLETED').length,
        in_progress: transformedCertifications.filter(c => c.status === 'IN_PROGRESS').length,
        not_started: transformedCertifications.filter(c => c.status === 'NOT_STARTED').length,
        expired: transformedCertifications.filter(c => c.status === 'EXPIRED').length,
        passed: transformedCertifications.filter(c => c.passed_exam).length,
        average_score: transformedCertifications.length > 0
          ? transformedCertifications.reduce((sum, c) => sum + (c.latest_exam_percentage || 0), 0) / transformedCertifications.length
          : 0,
      };

      // Return in expected DTO format
      return {
        user_id: userId,
        user_name: '',  // Not provided by API
        email: '',  // Not provided by API
        certifications: transformedCertifications,
        summary,
      };
    } catch (error) {
      console.error(`Error fetching certification details for user ${userId}:`, error);
      throw error;
    }
  }

  /**
   * Get detailed exam attempts with questions and answers for a specific certification
   * Calls backend method: listUserAttempts
   * @param userId - The unique identifier of the user
   * @param certificationId - The unique identifier of the certification
   * @returns Promise with array of detailed exam attempts
   */
  async getUserExamAttempts(
    userId: string,
    certificationId: string
  ): Promise<ExamAttemptDetailed[]> {
    try {
      // POST call with certification_id in body
      const url = API_ENDPOINTS.CERTIFICATION_TRACKING.USER_PROGRESS(userId);
      console.log('Fetching exam attempts from:', url);

      const response = await apiClient.post<APIResponse<any[]>>(url, {
        certification_id: certificationId
      });
      console.log('Exam attempts response:', response.data);

      if (!response.data.success) {
        throw new Error(response.data.description || 'Failed to fetch exam attempts');
      }

      const apiAttempts = response.data.data || [];
      console.log(`Retrieved ${apiAttempts.length} exam attempts`);

      // Transform API response to match UI expectations
      const transformedAttempts: ExamAttemptDetailed[] = apiAttempts.map((attempt: any) => {
        // Transform question_attempts to separate questions and answers arrays
        const questions = (attempt.question_attempts || []).map((qa: any) => ({
          question_id: qa.question_id,
          question_text: qa.question_text,
          question_type: qa.question_type,
          points: qa.max_points,
          options: qa.options || [],
          explanation: qa.explanation,
          correct_answer: qa.correct_answer, // Include correct answer from backend
          // Include answer-related fields from question_attempts
          answer_text: qa.answer_text,
          answer_submitted: qa.answer_submitted,
          is_correct: qa.is_correct,
          points_earned: qa.points_earned,
          max_points: qa.max_points,
          time_spent_seconds: qa.time_spent_seconds,
          submitted_at: qa.submitted_at,
        }));

        const answers = (attempt.question_attempts || []).map((qa: any) => ({
          question_id: qa.question_id,
          // Unanswered questions have a null answer; keep the list empty so "not answered" shows
          selected_option_ids: Array.isArray(qa.answer_submitted)
            ? qa.answer_submitted
            : qa.answer_submitted != null ? [qa.answer_submitted] : [],
          text_answer: typeof qa.answer_submitted === 'string' && qa.question_type === 'TEXT_ANSWER'
            ? qa.answer_submitted
            : undefined,
          answer_text: qa.answer_text, // Add the human-readable answer text
          is_correct: qa.is_correct,
          points_earned: qa.points_earned,
          points_possible: qa.max_points,
        }));

        // Calculate score and percentage if not provided
        const totalPoints = questions.reduce((sum, q) => sum + (q.points || 0), 0);
        const earnedPoints = answers.reduce((sum, a) => sum + (a.points_earned || 0), 0);
        const percentage = totalPoints > 0 ? Math.round((earnedPoints / totalPoints) * 100) : 0;

        return {
          attempt_id: attempt.id,
          user_id: attempt.user_id,
          certification_id: attempt.certification_id,
          certification_exam_id: attempt.certification_exam_id,
          attempt_number: attempt.attempt_number,
          status: attempt.status,
          started_at: attempt.started_at,
          completed_at: attempt.completed_at || attempt.submitted_at,
          time_spent_seconds: attempt.time_spent_seconds,
          overall_time_spent_seconds: attempt.overall_time_spent_seconds || 0,
          score: attempt.score ?? earnedPoints,
          percentage: attempt.percentage ?? percentage,
          passed: attempt.passed ?? (percentage >= 70), // Assume 70% passing if not provided
          answers,
          questions,
        };
      });

      return transformedAttempts;
    } catch (error) {
      console.error(`Error fetching exam attempts for user ${userId}, certification ${certificationId}:`, error);
      throw error;
    }
  }
  async updateTrackingSettings(trackingId: string, data: {
    is_mandatory?: boolean;
    deadline_date?: string;
    send_notification?: boolean;
    enable_automatic_reminder?: boolean;
    reminder_frequency_days?: number | null;
  }) {
    const response = await apiClient.put(API_ENDPOINTS.CERTIFICATION_TRACKING.UPDATE_SETTINGS(trackingId), data);
    return response.data?.data || response.data;
  }

  async revokeCertificationForUser(certificationId: string, userId: string) {
    try {
      const response = await apiClient.delete(
        API_ENDPOINTS.CERTIFICATION_TRACKING.REVOKE(certificationId, userId)
      );
      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.description || 'Failed to revoke certification');
    } catch (error: any) {
      throw error;
    }
  }
}

// Export singleton instance
export const certificationPerformanceService = new CertificationPerformanceService();
